import type { ButtonHTMLAttributes, ReactNode } from 'react';

import styles from './Button.module.css';
import focus from './focus.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'destructive';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  /** Full width, for the primary action at the bottom of a sheet. */
  readonly block?: boolean;
  /** Shows a spinner and blocks further presses. The label stays, so the button does not change size. */
  readonly busy?: boolean;
  readonly children: ReactNode;
}

/**
 * The button. A control says exactly what will happen (STD-002 §7), so the label is the verb and there is
 * no `icon-only` variant without an accessible name.
 *
 * `busy` disables rather than hiding: a button that vanishes mid-press moves everything under the thumb.
 */
export function Button({
  variant = 'primary',
  block = false,
  busy = false,
  disabled = false,
  type = 'button',
  className,
  children,
  ...rest
}: ButtonProps): React.JSX.Element {
  const classes = [styles.button, styles[variant], focus.focusable, block ? styles.block : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className={styles.spinner} aria-hidden="true" /> : null}
      {children}
    </button>
  );
}
